import { Assets, resolveJsonUrl, spritesheetAsset, extensions } from 'pixi.js';
import type { AssetsManifest } from 'pixi.js';
import { Signal } from 'typed-signals';
import assetManifest from '../../assets-manifest.json';
import type { Platform } from '../platform/Platform';
import { repeatUntilSuccessful } from '../../utils/async-utils';
import { applyLoadTextureRejectFix } from './applyLoadTextureRejectFix';

applyLoadTextureRejectFix();

export type ProgressFunction = (progress: number) => void;

export class Preload {
  public readonly name = 'Preload';

  public onLoadProgressUpdate = new Signal<(progress: number) => void>();

  private assetsManifest: AssetsManifest = { bundles: [] };

  private readonly loadedBundles: string[] = [];

  public async init(platform: Platform, onProgress?: ProgressFunction): Promise<void> {
    // To fix image resolution for atlases with .avif and .webp
    extensions.remove(resolveJsonUrl);
    extensions.add(spritesheetAsset);
    
    // Add platform-specific avatar parser if available
    const avatarParser = platform.getAvatarParser();
    if (avatarParser) {
      extensions.add(avatarParser);
    }

    // Load assets manifest
    this.assetsManifest = assetManifest;

    // Init PixiJS assets with this asset manifest
    await Assets.init({
      manifest: this.assetsManifest,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      basePath: (window as unknown as { PRELOAD_BASE_PATH: string }).PRELOAD_BASE_PATH ?? 'assets',
      texturePreference: {
        format: ['avif', 'webp', 'png'],
      },
      skipDetections: false,
    });

    await this.loadBundles(
      [
        'upsell',
        'default',
        'game',
        'popups',
        'ui',
        'animations',
        'menu',
        'shop',
        'tournamentPage',
        'dailyReward',
        'collectEvent',
        'duel',
        // 'winStreak',
        // 'raceEvent',
        // 'planks',
        // 'seasonPass',
      ],
      (bundleLoadProgress) => {
        let progress = bundleLoadProgress;
        progress += 75;

        if (progress > 99) {
          progress = 99;
        }

        if (onProgress) {
          onProgress(progress);
        }

        this.onLoadProgressUpdate.emit(progress);
      },
    );
  }

  private checkBundleExists(bundle: string): boolean {
    return !!this.assetsManifest.bundles.find((b) => b.name === bundle);
  }

  public async loadBundles(bundles: string[], onProgress?: ProgressFunction): Promise<void> {
    Object.values(bundles).forEach((bundle) => {
      if (!this.checkBundleExists(bundle)) {
        throw new Error(`[Assets] Invalid bundle: ${bundle}`);
      }
    });

    const loadList = bundles.filter((bundle) => !this.loadedBundles.includes(bundle));

    if (!loadList.length) return;

    await repeatUntilSuccessful(async () => {
      await Assets.loadBundle(loadList, (progress) => {
        if (onProgress) onProgress(progress * 100);
      });
    }, 500);

    if (onProgress) onProgress(100);

    this.loadedBundles.push(...loadList);
  }

  public bundlesLoaded(bundles: string[]): boolean {
    for (let i = 0; i < bundles.length; i += 1) {
      const bundle = bundles[i];
      if (!this.loadedBundles.includes(bundle)) {
        return false;
      }
    }
    return true;
  }
}
